/**
 * 무대 위의 체인저 도구 - 바꾸기 진입에 세우고 수색 진입에 치운다.
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(바꾸기 1분) · 4.2절(선택 → 동사 → 즉시 실행)
 *            `이상한방-상태모델-v0.1.md` 9.4절(감춘 도구는 언젠가 다시 드러난다)
 *            로드맵 M4(`stage.ts` - 바꾸기 페이즈 진입 효과에서 도구를 세우고 수색 진입에서 dispose. **수색 중에는 만들지 않는다**)
 *
 * `stage.ts`에서 갈라져 나왔다(400줄 목표를 넘기지 않으려고). 하는 일은 조립 하나다 - 도구
 * (`input/changerTool.ts`) · 띠(`ui/changerBar.ts`) · 선택 외곽선(`render/selectionOutline.ts`)을 세션의 문 넷
 * (`MatchChangerPort`)에 묶는다. 규칙은 없다. 무엇이 잠기는지는 도구가, 무엇이 수용되는지는 호스트가 정한다.
 *
 * ## 세우는 순간이 곧 규약이다
 *
 * 도구는 `phaseEnter{change}`에 **내가 다녀간 사람일 때만** 서고, 다른 페이즈에 들어서는 순간 통째로 사라진다. 배치 에디터를
 * `?dev=1` 없이 만들지 않는 것과 같은 결정이다 - 감춘 띠는 언젠가 수색 중에 다시 드러나고, 그때 그것은 정답을 여는 문이다.
 * 그래서 `hidden`이 아니라 `dispose`다. 세션은 이 도구를 조회 함수(`current`)로만 알고 값으로 들지 않는다.
 */

import type { Object3D } from 'three'

import { PALETTE } from '../catalog/palette'
import type { Catalog } from '../core/room'
import type { PlayerId, RoomBounds, RoomConfig } from '../core/types'
import type { MatchChangerPort } from '../game/matchSessionChanger'
import type { MatchPhase } from '../game/match/matchTypes'
import { createChangerTool, type ChangerTool } from '../input/changerTool'
import { EXACT_TAP_RADIUS_PX, pickAt } from '../input/tapPicker'
import { createGhostMesh, updateGhost } from '../render/gridHelper'
import type { SceneCtx } from '../render/scene'
import {
  createSelectionOutline,
  createZoneOutline,
  disposeSelectionOutline,
  placeSelectionOutline,
  placeZoneOutline,
} from '../render/selectionOutline'
import { createChangerBar } from '../ui/changerBar'
import { createChangerCoach } from '../ui/changerCoach'

export type StageChangerDeps = Readonly<{
  ctx: SceneCtx
  catalog: Catalog
  bounds: RoomBounds
  /**
   * 허용 동사·개수의 조회. 값이 아니라 함수인 이유는 방장이 로비에서 `configure`로 바꾸기 때문이다(M6) - 도구가 서는 순간(바꾸기 진입)에
   * 뷰가 실어 준 지금 값을 읽는다(`stageMatch.roomConfigOf`). 굳힌 값이면 "옮김만"인 방에서 도구가 다섯 동사를 다 연다.
   */
  config: () => RoomConfig
  seed: string
  me: PlayerId
  mount: HTMLElement
  port: MatchChangerPort
  /**
   * 여럿이 첫 바꾸기 안내(`ui/changerCoach.ts`)를 세울 것인가. 계정에 남는 값이라 `main.ts`가 진행에서 읽어 준다 -
   * 무대가 `localStorage`를 알면 무대가 단계마다 다시 서는 파일이라는 사실과 곧바로 부딪친다.
   * **값이 아니라 함수**인 이유는 `config`와 같다: 도구가 서는 순간의 값을 읽어야 한 판 안에서 마친 안내가 다음 라운드에 다시 안 뜬다.
   */
  needsCoach: () => boolean
  /** 안내를 마쳤다. 계정에 남기는 것은 부르는 쪽의 몫이다. */
  onCoached: () => void
}>

export type StageChanger = Readonly<{
  /** 지금 서 있는 도구. 없으면 `null` - 손(`inputLayer`)과 세션이 이것으로 "지금 바꾸기 중인가"를 안다. */
  current: () => ChangerTool | null
  /** 페이즈가 바뀌었다. 뷰가 "작업대를 만질 수 있다"면 세우고, 아니면 치운다. 인자는 시점의 표식일 뿐 여기서 읽지 않는다. */
  onPhaseEnter: (phase: MatchPhase) => void
  dispose: () => void
}>

export function createStageChanger(deps: StageChangerDeps): StageChanger {
  const { ctx, catalog, bounds, port } = deps

  let mounted: Readonly<{ tool: ChangerTool; dispose: () => void }> | null = null

  function unmount(): void {
    mounted?.dispose()
    mounted = null
  }

  function mount(): void {
    unmount()
    const draft = port.draft()
    const original = port.original()
    // 방이 아직 안 왔으면 세울 것이 없다. 호스트는 `rooms`를 `step`보다 먼저 보내므로(`matchHost.broadcast`) 이 길은 지연의 것이다.
    if (draft === null || original === null) return

    const outline = createSelectionOutline()
    ctx.scene.add(outline)

    /*
     * 내 구획의 테두리(M11). 도구와 **수명이 같다** - 바꾸기 진입에 서고 수색 진입에 사라진다. 수색 중에 남으면
     * 그 선이 곧 "여기부터 저기까지는 내가 만든 것"이라 남들에게 정답 지도가 된다(도구를 감추지 않고 없애는 것과 같은 이유).
     * 사각형은 호스트가 배정한 값 그대로다(`snapshot().zoneRect`) - 무대가 좌표를 만들지 않는다.
     */
    const zone = createZoneOutline()
    ctx.scene.add(zone)
    placeZoneOutline(zone, port.snapshot().zoneRect)

    // 놓을 자리 고스트. 도구가 서 있는 동안만 씬에 있고 페이즈를 나갈 때 통째로 내려간다(감춘 도구 금지 규약).
    const ghost: Object3D = createGhostMesh()
    ghost.visible = false
    ctx.scene.add(ghost)

    // 띠의 버튼은 도구를 부른다. 도구가 띠보다 뒤에 만들어지므로 클로저로 늦게 잡는다 - 서로를 인자로 요구하면 순환이다.
    let tool: ChangerTool | null = null
    const bar = createChangerBar({
      mount: deps.mount,
      handlers: {
        onVerb: (verb) => tool?.pickVerb(verb),
        onCatalog: (id) => tool?.pickCatalog(id),
        onColor: (id) => tool?.pickColor(id),
        onUndo: () => tool?.undo(),
        // 잠긴 동사를 눌렀다. 문구는 띠가 이미 골랐고(이유 표가 거기 하나뿐이다) 여기는 띄울 곳으로 넘기기만 한다.
        onLocked: (text) => port.showLocked(text),
        onCommit: (wager) => tool?.commit(wager),
      },
    })

    /*
     * 첫 판 안내. 도구·띠와 **수명이 같다** - 바꾸기 진입에 서고 나갈 때 사라진다. 카드가 수색까지 남으면
     * "물건을 놓아라"가 쏘는 화면에 떠 있게 된다(감춘 도구 금지 규약의 같은 이유).
     * 띠와 마찬가지로 **도구보다 먼저** 세운다 - 도구는 만들어지는 도중에 `onView`를 부를 수 있다.
     */
    const coach = deps.needsCoach() ? createChangerCoach({ mount: deps.mount, onDone: deps.onCoached }) : null

    tool = createChangerTool({
      catalog,
      config: deps.config(),
      bounds,
      palette: PALETTE,
      seed: deps.seed,
      me: deps.me,
      initialDraft: draft,
      original,
      view: port.snapshot,
      // 물건 고르기도 자리 정하기도 배치와 같은 정확함이다(`tapPicker.ts` 머리말 "바꾸기의 물건 고르기도 배치 쪽이다").
      // 수색의 관대함(줌 반비례 링)을 여기 주면 전체 조망에서 바닥 소품을 누른 탭이 투영상 겹친 벽 물건(거울·TV)으로
      // 끌려간다(M4 플레이테스트). 두 번째 판정 경로가 아니라 같은 `search` 해석에 반경만 0이다 - 선택은 '물건 히트'만 쓴다.
      pick: (x, y, room, mode) =>
        pickAt(ctx, x, y, room, catalog, bounds, mode === 'place' ? { mode: 'place' } : { mode: 'search', radiusPx: EXACT_TAP_RADIUS_PX }),
      send: port.send,
      onDraft: port.showDraft,
      onView: (view) => {
        bar.render(view)
        // 안내는 띠와 **같은 뷰, 같은 순간**을 본다 - 두 곳이 각자 세면 "3/3인데 아직 놓으라고 한다"가 생긴다.
        coach?.render(view)
      },
      onLocalReject: port.showRejection,
      // 남의 구획 물건을 탭했다(M11). 거부가 아니라 아무 일도 없었음이라 다른 문을 지난다.
      onOutOfZone: port.showOutOfZone,
      onSelectionChanged: (obj, entry) => placeSelectionOutline(outline, obj, entry),
      /*
       * 놓을 자리 미리보기. 배치 에디터(`input/editor.ts`)와 **같은 고스트**다 - 위치·회전·표식 계산이
       * `objectView`와 공유되므로 "고스트에는 앞면이 있었는데 놓고 보니 없다"가 생기지 않는다
       * (`render/gridHelper.updateGhost` 머리말). 여기서 좌표를 다시 만들지 않는다.
       */
      onPreview: (preview) => {
        const entry = preview === null ? undefined : catalog.get(preview.catalogId)
        if (preview === null || entry === undefined) {
          ghost.visible = false
          return
        }
        updateGhost(ghost, preview.cell, entry, preview.rot, preview.valid)
      },
    })

    const built = tool
    mounted = {
      tool: built,
      dispose(): void {
        built.dispose()
        bar.dispose()
        coach?.dispose()
        ghost.removeFromParent()
        disposeSelectionOutline(outline)
        disposeSelectionOutline(zone)
      },
    }
  }

  return {
    current: () => mounted?.tool ?? null,

    onPhaseEnter(): void {
      // "바꾸기 페이즈이고 내가 다녀간 사람인가"는 호스트가 내린 뷰의 불리언 하나(`self.canChange` → `snapshot().active`)다 -
      // 여기서 `phase === 'change'`를 다시 대면 게이트의 사본이다. 페이즈 진입은 다시 볼 **시점**만 준다.
      if (port.snapshot().active) mount()
      else unmount()
    },

    dispose: unmount,
  }
}
